import { LoadingNote } from "../lib/Loading.tsx";
import { CONFIRMATION_COPY } from "./provenance.constants.ts";
import type { ConfirmationDetail } from "./provenance.model.ts";

/**
 * Who has put their name to this link, what they said, and when.
 *
 * Confirmations and disputes are one list, in the order they were made, and a
 * dispute is not moved to the bottom or folded away — a reader who sees only
 * the agreements has been told something the record does not say. Comments
 * follow as their own list, because a comment is context rather than a position
 * and changes no count above.
 *
 * A withdrawn row stays and is marked. The position was held, and dropping it
 * would leave the count above looking wrong to anyone who had seen it before.
 */
export function ProvenanceConfirmations({
  detail,
  onWithdraw,
  onWithdrawComment,
}: {
  detail: ConfirmationDetail;
  /**
   * Ends the reader's own live position.
   *
   * Offered ONLY on a row that is `mine` and not already withdrawn, because
   * those are the only rows the endpoint will accept — and because a control
   * beside somebody else's signature would suggest it could be taken down.
   */
  onWithdraw: (attestationId: string) => void;
  /**
   * Ends the reader's own live comment — OND-21. The same two conditions, for
   * the same two reasons; the route is `provenance-comments/:id/withdraw` and
   * refuses anybody else's row with one opaque 403.
   */
  onWithdrawComment: (commentId: string) => void;
}) {
  if (detail.kind === "idle") return null;

  if (detail.kind === "loading") {
    return <LoadingNote message={detail.message} />;
  }

  if (detail.kind === "failed") {
    return (
      <p className="provenance__acts-note" role="alert">
        {detail.message}
      </p>
    );
  }

  if (detail.emptyNote !== null) {
    return <p className="provenance__acts-note">{detail.emptyNote}</p>;
  }

  return (
    <div className="provenance__acts">
      {detail.attestations.length > 0 && (
        <section>
          <h4 className="provenance__acts-title">{CONFIRMATION_COPY.confirmationsTitle}</h4>
          <ul className="provenance__acts-list">
            {detail.attestations.map((attestation) => (
              <li
                key={attestation.id}
                className={`provenance__act provenance__act--${attestation.stance === "verify" ? "confirmed" : "disputed"}${
                  attestation.withdrawn ? " provenance__act--withdrawn" : ""
                }`}
              >
                <span className="provenance__act-stance">{attestation.stanceLabel}</span>
                <span className="provenance__act-by">
                  {attestation.byName}
                  <span className="provenance__act-ondo">{attestation.byOndoId}</span>
                  {attestation.mine && (
                    <span className="provenance__act-mine">{CONFIRMATION_COPY.you}</span>
                  )}
                </span>
                <span className="provenance__act-date">{attestation.date}</span>
                {attestation.body !== null && (
                  <q className="provenance__act-body">{attestation.body}</q>
                )}
                {attestation.withdrawn && (
                  <span className="provenance__act-withdrawn">
                    {CONFIRMATION_COPY.withdrawn}
                  </span>
                )}
                {/* The control `PROVENANCE_COPY.positionHeld` has been pointing
                    at since 20260804090500. Verify and Dispute both close once
                    either is held, so without this an account that confirmed an
                    entry and then found reason to dispute it had no route
                    between the two. */}
                {attestation.mine && !attestation.withdrawn && (
                  <button
                    type="button"
                    className="provenance__act-unsign"
                    onClick={() => onWithdraw(attestation.id)}
                  >
                    {CONFIRMATION_COPY.takeBack}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {detail.comments.length > 0 && (
        <section>
          <h4 className="provenance__acts-title">{CONFIRMATION_COPY.commentsTitle}</h4>
          <ul className="provenance__acts-list">
            {detail.comments.map((comment) => (
              <li
                key={comment.id}
                className={`provenance__act provenance__act--comment${
                  comment.withdrawn ? " provenance__act--withdrawn" : ""
                }`}
              >
                <span className="provenance__act-by">
                  {comment.byName}
                  <span className="provenance__act-ondo">{comment.byOndoId}</span>
                  {comment.mine && (
                    <span className="provenance__act-mine">{CONFIRMATION_COPY.you}</span>
                  )}
                </span>
                <span className="provenance__act-date">{comment.date}</span>
                <q className="provenance__act-body">{comment.body}</q>
                {comment.withdrawn && (
                  <span className="provenance__act-withdrawn">
                    {CONFIRMATION_COPY.commentWithdrawn}
                  </span>
                )}
                {/* OND-21. The route had no caller since 20260804090500, so a
                    comment was permanent in practice while the record's own
                    rule said it was not. Own and live only, as above. */}
                {comment.mine && !comment.withdrawn && (
                  <button
                    type="button"
                    className="provenance__act-unsign"
                    onClick={() => onWithdrawComment(comment.id)}
                  >
                    {CONFIRMATION_COPY.withdrawComment}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
